// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { CloseRounded } from '@/constants/hardfin-standard-icons';
import { CostAuditList } from '@/costs/components/cost-audit-list';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Suspense } from 'react';

interface Props {
  costKey: string;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Drawer component that displays the audit log for a specific cost.
 * Opens from the right side of the screen and shows a chronological list of audit events.
 */
export function CostAuditDrawer({ costKey, isOpen, onClose }: Props) {
  const theme = useTheme();

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{ sx: { maxWidth: '35rem', minWidth: '25rem' } }}
      sx={{ zIndex: theme.zIndex.modal }}
    >
      <Stack
        spacing={1}
        padding={2}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="subtitle1">Audit log</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseRounded fontSize="small" />
        </IconButton>
      </Stack>
      <Divider />
      <Box padding={2}>
        <SupportErrorBoundary message="An error occurred while loading the audit log.">
          <Suspense>
            <CostAuditList costKey={costKey} />
          </Suspense>
        </SupportErrorBoundary>
      </Box>
    </Drawer>
  );
}
